import { useState, useEffect } from 'react'
import {
  Dialog,
  Modal as AriaModal,
  ModalOverlay,
} from 'react-aria-components'
import { XCloseIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { OnboardingInput } from './ui/OnboardingInput'
import { CountrySelect } from './CountrySelect'

export interface CustomerFormData {
  companyName: string
  billingEmail: string
  billingAddress: string
  billingCity: string
  billingPostalCode: string
  billingCountry: string
}

interface EditCustomerModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: CustomerFormData) => void
  data: CustomerFormData
}

export function EditCustomerModal({
  isOpen,
  onClose,
  onSave,
  data,
}: EditCustomerModalProps) {
  const [formData, setFormData] = useState<CustomerFormData>(data)

  useEffect(() => {
    if (isOpen) {
      setFormData(data)
    }
  }, [isOpen, data])

  const handleChange = (field: keyof CustomerFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = () => {
    onSave(formData)
    onClose()
  }

  if (!isOpen) return null

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      isDismissable
      className={cn(
        'onboarding-layout fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4',
        'bg-[rgba(10,13,18,0.72)]',
        'entering:animate-in entering:fade-in-0',
        'exiting:animate-out exiting:fade-out-0'
      )}
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <AriaModal
        className={cn(
          'w-full max-w-[512px] rounded-2xl bg-white shadow-xl outline-none',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
        )}
      >
        <Dialog aria-label="Edit customer information" className="outline-none">
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <button type="button" autoFocus className="sr-only" aria-hidden="true" tabIndex={-1} />
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
            {/* Header */}
            <div className="relative px-6 pb-5 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="absolute right-4 top-4 flex cursor-pointer items-center justify-center rounded-lg p-2 text-[#535862] transition-colors hover:bg-[#f9fafb]"
              >
                <XCloseIcon className="h-6 w-6" />
              </button>

              <div className="flex flex-col gap-0.5">
                <h2 className="text-base font-semibold leading-6 text-[#181d27]">
                  Edit customer information
                </h2>
                <p className="text-sm font-normal leading-5 text-[#535862]">
                  Make sure everything looks right before continuing.
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="flex flex-col gap-5 px-6 pb-0">
              <OnboardingInput
                label="Company name"
                type="text"
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                placeholder="Your company or business name"
              />

              <OnboardingInput
                label="Billing email"
                type="email"
                value={formData.billingEmail}
                onChange={(e) => handleChange('billingEmail', e.target.value)}
                placeholder="billing@company.com"
              />

              <OnboardingInput
                label="Billing address"
                type="text"
                value={formData.billingAddress}
                onChange={(e) => handleChange('billingAddress', e.target.value)}
                placeholder="Street and number"
              />

              <div className="flex gap-4">
                <div className="flex-1">
                  <OnboardingInput
                    label="City"
                    type="text"
                    value={formData.billingCity}
                    onChange={(e) => handleChange('billingCity', e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="w-[130px]">
                  <OnboardingInput
                    label="Postal code"
                    type="text"
                    value={formData.billingPostalCode}
                    onChange={(e) => handleChange('billingPostalCode', e.target.value)}
                    placeholder="1234 AB"
                  />
                </div>
              </div>

              <CountrySelect
                value={formData.billingCountry}
                onChange={(val) => handleChange('billingCountry', val)}
                label="Country"
              />
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 pb-6 pt-8">
              <button
                type="button"
                onClick={onClose}
                className="flex flex-1 cursor-pointer items-center justify-center rounded-lg border border-[#d5d7da] bg-white px-4 py-2.5 text-base font-semibold text-[#414651] shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] transition-colors hover:bg-[#f9fafb]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex flex-1 cursor-pointer items-center justify-center rounded-lg bg-[#181d27] px-4 py-2.5 text-base font-semibold text-white transition-colors hover:bg-[#2d3340]"
              >
                Save
              </button>
            </div>
          </form>
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  )
}
